import { useMemo } from "react";
import { Card, CardContent, CardHeader, Typography, CodeBlock } from "@altimateai/lego";
import { CopyButton } from "../../components/copyButton";
import { MCPIntegrationType } from "../mcp/constants";
import useIntegrations from "./useIntegrations";
import ViewConnections from "../mcp/ViewConnections";
import { getTenant } from "@/helpers/tenant";
import { API_URL } from "@/config";
import { useAppState } from "@/modules/app/useAppContext";
import { useConnections } from "@/hooks/useConnections";
import { Teammate } from "../mcp/mcpSlice";

interface AltimateCodeInstructionsProps {
  teammate: Teammate;
}

const AltimateCodeInstructions = ({ teammate }: AltimateCodeInstructionsProps) => {
  const { loggedinUser: user } = useAppState();

  const { allIntegrations, isCustomIntegrationsLoading, isIntegrationsFromServerLoading } =
    useIntegrations();

  const {
    selectedConnections,
    handleConnectionSelect,
    isLoading: isConnectionsLoading,
  } = useConnections({
    integrations: teammate?.integrations?.map(int => int.id) || [],
  });

  const isLoading =
    isCustomIntegrationsLoading || isIntegrationsFromServerLoading || isConnectionsLoading;

  const datamateIntegrationsWithData = useMemo(
    () =>
      teammate?.integrations
        ?.map(datamateIntegration => {
          const integrationData = allIntegrations.find(
            integration => integration.id === datamateIntegration.id
          );
          return integrationData;
        })
        .filter(Boolean) || [],
    [teammate?.integrations, allIntegrations]
  );

  const serverName = useMemo(() => {
    const name = teammate?.name?.toLowerCase().replace(/\s+/g, "-") || "datamate";
    return `datamate-${name}`;
  }, [teammate?.name]);

  const buildCommand = useMemo(() => {
    const token = user?.auth_token || "YOUR_TOKEN";
    const datamateId = teammate?.id?.toString() || "YOUR_DATAMATE_ID";
    const tenant = getTenant() || "YOUR_TENANT";
    const apiUrl = API_URL || "YOUR_API_URL";

    const headers = [
      `--header "Authorization=Bearer ${token}"`,
      `--header "x-datamate-id=${datamateId}"`,
      `--header "x-tenant=${tenant}"`,
      `--header "x-altimate-url=${apiUrl}"`,
    ];

    Object.entries(selectedConnections).forEach(([integrationId, connectionId]) => {
      const integration = datamateIntegrationsWithData.find(int => int?.id === integrationId);
      if (integration?.name) {
        const headerKey = `x-connection-${integration.name.toLowerCase().replace(/\s+/g, "-")}`;
        headers.push(`--header "${headerKey}=${connectionId}"`);
      }
    });

    return (scope: "global" | "project") => {
      const flags = [
        `--name "${serverName}"`,
        `--type remote`,
        `--url "https://mcpserver.getaltimate.com/sse"`,
        ...headers,
        `--no-oauth`,
        ...(scope === "global" ? [`--global`] : []),
      ];
      return `altimate-code mcp add ${flags.join(" ")}`;
    };
  }, [teammate, user, selectedConnections, datamateIntegrationsWithData, serverName]);

  const globalCommand = useMemo(() => buildCommand("global"), [buildCommand]);
  const projectCommand = useMemo(() => buildCommand("project"), [buildCommand]);

  return (
    <div className="al-max-w-3xl al-mx-auto">
      <Typography variant="body">
        Follow these steps to add this datamate to Altimate Code:
      </Typography>

      <div className="al-space-y-6">
        <Card>
          <CardHeader className="!al-p-3 !al-pb-0">
            <Typography variant="section-heading">1. Select Connections</Typography>
          </CardHeader>
          <CardContent className="!al-p-3">
            <Typography variant="body" className="al-text-gray-700 al-mb-4">
              Choose the connections you want to use for each integration. The first connection per
              integration is pre-selected.
            </Typography>
            <div className="al-space-y-4">
              {isLoading ? (
                <Typography variant="body" className="al-text-sm al-text-muted-foreground">
                  Loading integrations...
                </Typography>
              ) : (
                <>
                  {datamateIntegrationsWithData.length === 0 ? (
                    <Typography variant="body" className="al-text-sm al-text-muted-foreground">
                      No integrations configured
                    </Typography>
                  ) : (
                    datamateIntegrationsWithData.map(integration => {
                      if (integration?.type === MCPIntegrationType.EXTENSION) return null;

                      return (
                        <div key={integration?.id} className="al-border al-rounded-lg al-p-4">
                          <Typography variant="body" className="al-mb-2">
                            {integration?.name}
                          </Typography>
                          <ViewConnections
                            integration={integration!}
                            onSelect={handleConnectionSelect}
                            selectedConnectionId={selectedConnections[integration?.id || ""]}
                          />
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="!al-p-3 !al-pb-0">
            <Typography variant="section-heading">2. Add globally</Typography>
          </CardHeader>
          <CardContent className="!al-p-3">
            <Typography variant="body" className="al-text-gray-700 al-mb-4">
              Available across all projects. Run in your terminal:
            </Typography>
            <div className="al-space-y-4">
              <CodeBlock
                code={globalCommand}
                language="javascript"
                theme="light"
                editorTheme="tomorrow"
              />
              <CopyButton value={globalCommand} label="Copy Command" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="!al-p-3 !al-pb-0">
            <Typography variant="section-heading">Or add to current project only</Typography>
          </CardHeader>
          <CardContent className="!al-p-3">
            <Typography variant="body" className="al-text-gray-700 al-mb-4">
              Run from your project directory to add the datamate to this project only:
            </Typography>
            <div className="al-space-y-4">
              <CodeBlock
                code={projectCommand}
                language="javascript"
                theme="light"
                editorTheme="tomorrow"
              />
              <CopyButton value={projectCommand} label="Copy Command" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AltimateCodeInstructions;
